import React, { Suspense, useEffect, useId, useRef, useState } from 'react';

import { StyledSingleDayPicker } from './SingleDayPicker.styles';
import { SingleDayPickerProps } from './SingleDayPicker.types';

import type DayPicker from 'react-day-picker';
import type { CaptionElementProps, DayModifiers } from 'react-day-picker';

import { Text, setMomentLocale, size, tags } from '@marriott/mi-ui-library';
import clsx from 'classnames';
import moment from 'moment';
import { NavbarElementProps } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { useFocusOut } from '../../hooks/useFocusOut';
import { useLegalGenericStore } from '../../store';

const LazyDayPicker = React.lazy(() => import('react-day-picker'));

export const SingleDayPicker = ({
  placeholder,
  label,
  errorMessage = '',
  dateFormat,
  headerDateFormat,
  defaultDate,
  onDayClick,
  className,
  dayPickerPros = {},
  disabled = false,
  getInputProps,
}: SingleDayPickerProps) => {
  const id = useId();
  const [show, setShow] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(defaultDate);
  const dayPickerRef = useRef<DayPicker>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useFocusOut({
    ref: datePickerRef.current,
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut) {
        setShow(false);
      }
    },
    handleKeyup: true,
  });

  const { onKeyDown } = dayPickerPros;

  const showCalendar = () => setShow(true);
  const hideCalendar = () => setShow(false);
  const focusInput = () => inputRef.current?.focus();
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      showCalendar();
    } else if (e.code === 'Escape') {
      hideCalendar();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Escape') {
      hideCalendar();
      focusInput();
    }
    onKeyDown?.(e);
  };

  const handleDayClick = (
    day: Date,
    { selected, outside, disabled }: DayModifiers,
    _e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    if (disabled || outside) return;
    const selectedDay = selected ? undefined : day;
    setSelectedDay(selectedDay);
    onDayClick(selectedDay);
    hideCalendar();
    focusInput();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close the date picker only when clicked outside
      if (
        dayPickerRef.current &&
        event.target instanceof Node &&
        !dayPickerRef.current.dayPicker.contains(event.target)
      ) {
        hideCalendar();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // remove tabindex from calendar wrapper to fix accessibility issues
  useEffect(() => {
    if (dayPickerRef.current?.dayPicker.firstElementChild?.getAttribute('tabindex')) {
      dayPickerRef.current?.dayPicker.firstElementChild.removeAttribute('tabindex');
    }
  }, [show]);

  useEffect(() => {
    setSelectedDay(defaultDate);
  }, [defaultDate]);

  const { locale } = useLegalGenericStore();
  useEffect(() => {
    if (!locale) return;
    setMomentLocale(locale.toLowerCase());
  }, [locale]);

  if (headerDateFormat) {
    dayPickerPros.captionElement = ({ date, locale }: CaptionElementProps) => (
      <div className="DayPicker-Caption">{moment(date).locale(locale).format(headerDateFormat)}</div>
    );
  }

  if (!dateFormat) {
    dateFormat = 'ddd, MMM DD, YYYY';
  }

  return (
    <StyledSingleDayPicker
      data-component-name="m-legal-LazyDayPicker"
      data-testid="legal-LazyDayPicker"
      className={className}
      ref={datePickerRef}
    >
      <div className={clsx('m-input-field', { 'is-error': errorMessage })}>
        <label htmlFor={id} id={`date-picker-${id}`}>
          <span className="icon-calendar"></span>
          <span>{label}</span>
        </label>
        <input
          ref={inputRef}
          type="text"
          id={id}
          readOnly
          placeholder={placeholder}
          onClick={showCalendar}
          onKeyDown={handleInputKeyDown}
          aria-describedby={`date-picker-${id}`}
          aria-invalid={Boolean(errorMessage)}
          value={selectedDay ? moment(selectedDay).locale(locale).format(dateFormat) : ''}
          disabled={disabled}
          {...getInputProps?.()}
        />

        <span className={clsx('icon-arrow-down', { disabled })} aria-label="Choose Date" onClick={showCalendar} />
        {errorMessage && (
          <Text fontSize={size.small} element={tags.span} copyText={errorMessage} customClass="error-text" />
        )}
      </div>
      {show && (
        <Suspense fallback={<div>Loading Calendar ...</div>}>
          <LazyDayPicker
            ref={dayPickerRef}
            navbarElement={Navbar}
            {...dayPickerPros} // overwrite the above default values
            localeUtils={MomentLocaleUtils}
            locale={locale}
            onDayClick={handleDayClick}
            selectedDays={selectedDay}
            onKeyDown={handleKeyDown}
          />
        </Suspense>
      )}
    </StyledSingleDayPicker>
  );
};

function Navbar({ onPreviousClick, onNextClick, className, showPreviousButton, showNextButton }: NavbarElementProps) {
  const isRTL = document?.dir === 'rtl';
  const leftButton = {
    label: isRTL ? 'Next Month' : 'Previous Month',
    disabled: isRTL ? !showNextButton : !showPreviousButton,
    onClick: () => (isRTL ? onNextClick() : onPreviousClick()),
    style: { float: 'left' },
  };
  const rightButton = {
    label: isRTL ? 'Previous Month' : 'Next Month',
    disabled: isRTL ? !showPreviousButton : !showNextButton,
    onClick: () => (isRTL ? onPreviousClick() : onNextClick()),
    style: { float: 'right' },
  };

  return (
    <div className={className}>
      <button
        aria-label={leftButton.label}
        className="DayPicker-Nav-button"
        disabled={leftButton.disabled}
        onClick={leftButton.onClick}
        style={{ float: 'left' }}
        type="button"
      >
        
      </button>
      <button
        aria-label={rightButton.label}
        className="DayPicker-Nav-button"
        disabled={rightButton.disabled}
        onClick={rightButton.onClick}
        style={{ float: 'right' }}
        type="button"
      >
        
      </button>
    </div>
  );
}
